import React, { useState } from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiArrowLeft, FiHome, FiImage, FiCalendar, FiTrash2, FiCheck, FiX, FiLink } from 'react-icons/fi';

export default function FacilityEdit({ facility, organizations = [], amenities = [], user }) {
  const { data, setData, patch, processing, errors } = useForm({
    name: facility.name || '',
    description: facility.description || '',
    organization_id: facility.organization_id ? facility.organization_id.toString() : '',
    care_type: facility.care_type || 'assisted_living',
    address: facility.address || '',
    published: facility.published || false,
    calendly_url: facility.calendly_url || '',
    main_photo: null,
    photos: [],
    amenity_ids: facility.amenities ? facility.amenities.map(a => a.id) : []
  });

  const [previewImage, setPreviewImage] = useState(facility.main_photo_url || null);
  const [photosPreviews, setPhotosPreviews] = useState(facility.photos_urls || []);
  const [activeTab, setActiveTab] = useState('details');

  const handleSubmit = (e) => {
    e.preventDefault();
    patch(`/facility_owner/facilities/${facility.id}`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setData('main_photo', file);
    
    // Create preview URL
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setData('photos', files);
    
    // Create preview URLs for all selected files
    const newPreviews = [];
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === files.length) {
          setPhotosPreviews([...photosPreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };
  
  const handleAmenityChange = (amenityId) => {
    const currentAmenities = [...data.amenity_ids];
    
    if (currentAmenities.includes(amenityId)) {
      // Remove amenity if already selected
      setData('amenity_ids', currentAmenities.filter(id => id !== amenityId));
    } else {
      // Add amenity if not already selected
      setData('amenity_ids', [...currentAmenities, amenityId]);
    }
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href={`/facility_owner/facilities/${facility.id}`}>
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Facility
              </Link>
            </Button>
            
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Edit Facility</h1>
                <p className="text-gray-500">Update information for {facility.name}</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="details">Basic Details</TabsTrigger>
                <TabsTrigger value="photos">Photos</TabsTrigger>
                <TabsTrigger value="amenities">Amenities</TabsTrigger>
                <TabsTrigger value="scheduling">Scheduling</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Basic Information</CardTitle>
                    <CardDescription>Edit the basic details of your facility</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="name">Facility Name</Label>
                      <Input
                        id="name"
                        type="text"
                        value={data.name}
                        onChange={e => setData('name', e.target.value)}
                        className={errors.name ? 'border-red-500' : ''}
                      />
                      {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={data.description}
                        onChange={e => setData('description', e.target.value)}
                        rows={5}
                        className={errors.description ? 'border-red-500' : ''}
                      />
                      {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="organization_id">Organization</Label>
                      <Select
                        value={data.organization_id}
                        onValueChange={(value) => setData('organization_id', value)}
                      >
                        <SelectTrigger className={errors.organization_id ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select an organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id.toString()}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.organization_id && <p className="text-red-500 text-sm mt-1">{errors.organization_id}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="care_type">Care Type</Label>
                      <Select
                        value={data.care_type}
                        onValueChange={(value) => setData('care_type', value)}
                      >
                        <SelectTrigger className={errors.care_type ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select care type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="assisted_living">Assisted Living</SelectItem>
                          <SelectItem value="memory_care">Memory Care</SelectItem>
                          <SelectItem value="independent_living">Independent Living</SelectItem>
                          <SelectItem value="nursing_home">Nursing Home</SelectItem>
                          <SelectItem value="continuing_care">Continuing Care</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.care_type && <p className="text-red-500 text-sm mt-1">{errors.care_type}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        type="text"
                        value={data.address}
                        onChange={e => setData('address', e.target.value)}
                        className={errors.address ? 'border-red-500' : ''}
                      />
                      {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="published"
                        checked={data.published}
                        onCheckedChange={(checked) => setData('published', checked)}
                      />
                      <Label htmlFor="published">Publish this facility (make it visible to the public)</Label>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="photos" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Facility Photos</CardTitle>
                    <CardDescription>Upload photos of your facility</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label htmlFor="main_photo" className="text-lg font-medium">Main Photo</Label>
                      <p className="text-gray-500 text-sm mb-2">This will be the primary photo displayed for your facility</p>
                      {previewImage && (
                        <div className="mb-4">
                          <img 
                            src={previewImage} 
                            alt="Facility main photo" 
                            className="w-full h-64 object-cover rounded-md border"
                          />
                        </div>
                      )}
                      <Input
                        id="main_photo"
                        type="file"
                        onChange={handleFileChange}
                        className={errors.main_photo ? 'border-red-500' : ''}
                        accept="image/*"
                      />
                      {errors.main_photo && <p className="text-red-500 text-sm mt-1">{errors.main_photo}</p>}
                    </div>
                    
                    <div className="border-t pt-6">
                      <Label htmlFor="photos" className="text-lg font-medium">Additional Photos</Label>
                      <p className="text-gray-500 text-sm mb-4">Upload multiple photos to showcase your facility</p>
                      
                      {photosPreviews.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                          {photosPreviews.map((photo, index) => (
                            <div key={index} className="relative">
                              <img 
                                src={photo} 
                                alt={`Facility photo ${index + 1}`} 
                                className="w-full h-32 object-cover rounded-md border"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <Input
                        id="photos"
                        type="file"
                        onChange={handleMultipleFileChange}
                        className={errors.photos ? 'border-red-500' : ''}
                        multiple
                        accept="image/*"
                      />
                      {errors.photos && <p className="text-red-500 text-sm mt-1">{errors.photos}</p>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="amenities" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Facility Amenities</CardTitle>
                    <CardDescription>Select the amenities available at your facility</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {amenities.map((amenity) => (
                        <div key={amenity.id} className="flex items-start space-x-3 p-3 border rounded-md hover:bg-gray-50">
                          <Checkbox 
                            id={`amenity-${amenity.id}`}
                            checked={data.amenity_ids.includes(amenity.id)}
                            onCheckedChange={() => handleAmenityChange(amenity.id)}
                          />
                          <div>
                            <Label 
                              htmlFor={`amenity-${amenity.id}`}
                              className="font-medium cursor-pointer"
                            >
                              {amenity.name}
                            </Label>
                            <p className="text-sm text-gray-500">{amenity.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="scheduling" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Scheduling Options</CardTitle>
                    <CardDescription>Configure scheduling and tour options</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="calendly_url">Calendly URL</Label>
                        <div className="flex items-center">
                          <FiLink className="h-5 w-5 text-gray-400 mr-2" />
                          <Input
                            id="calendly_url"
                            type="text"
                            value={data.calendly_url}
                            onChange={e => setData('calendly_url', e.target.value)}
                            placeholder="https://calendly.com/your-facility"
                            className={errors.calendly_url ? 'border-red-500' : ''}
                          />
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Connect your Calendly account to allow visitors to schedule tours online</p>
                        {errors.calendly_url && <p className="text-red-500 text-sm mt-1">{errors.calendly_url}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" asChild>
                <Link href={`/facility_owner/facilities/${facility.id}`}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                Update Facility
              </Button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
}
